import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { SWReactIcons } from '@similarweb/icons';

import { Checkbox } from 'components/Checkbox';
import { useQuota } from 'features/quota/hooks/useQuota';
import { useUserStatus } from 'hooks/useUserStatus';
import { appStore } from 'services/appStore';

import { useAppState } from '../../../AppStateProvider';
import type { Contact, ContactVariant } from '../../../domain/contact';
import { hasDataToReveal, hasMatchedProfiles, hasPrivateData as checkHasPrivateData } from '../../../domain/contact';
import { useContactsAvailabilityStatus } from '../../../features/contacts/hooks/useContactsAvailabilityStatus';

import { ContactActionsContainer } from './ContactActionsContainer';
import { ContactGrade } from './ContactGrade';
import { PersonaFit } from './PersonaFit';
import { BlurredPrivateDataView, PrivateDataView } from './PrivateDataView';
import {
  StyledContactActionsContainer,
  StyledContactCountry,
  StyledContactDetails,
  StyledContactHeader,
  StyledContactItem,
  StyledContactItemColumn,
  StyledContactItemContainer,
  StyledContactLinkedinTooltip,
  StyledContactName,
  StyledContactTitle,
} from './styles';
import { useContactItemTracking } from './useContactItemTracking';

export type ContactItemProps = {
  contact: Contact;
  variant?: ContactVariant;
  withCompanyName?: boolean;
  shouldAnimatePrivateDataMount?: boolean;
};

export const ContactItem: FC<ContactItemProps> = (props) => {
  const { t } = useTranslation();
  const { contact, variant = 'listItem', withCompanyName = false } = props;
  const { selectedContacts, notRevealedContactsIds } = useAppState();

  const isRevealError = notRevealedContactsIds.includes(contact.id);
  const isRevealAvailable = hasDataToReveal(contact);

  const isSelected = useMemo(
    () => selectedContacts.some((selectedContact) => selectedContact.id === contact.id),
    [selectedContacts, contact.id]
  );

  const { isTrialOrAfterTrial } = useUserStatus();
  const { isGdprRestricted } = useContactsAvailabilityStatus();
  const hasPrivateData = checkHasPrivateData(contact) && !isGdprRestricted;
  const [shouldAnimatePrivateDataMount, setShouldAnimatePrivateDataMount] = useState(
    props.shouldAnimatePrivateDataMount || false
  );

  const { quota } = useQuota();

  useEffect(() => {
    if (props.shouldAnimatePrivateDataMount) {
      setShouldAnimatePrivateDataMount(props.shouldAnimatePrivateDataMount);
    }
  }, [props.shouldAnimatePrivateDataMount]);

  const { handleTrackCopyClick, handleTrackLinkedinLinkClick, handleTrackChipHover } = useContactItemTracking(contact);

  const { salesforceContactUrl, salesforceLeadUrl, hubspotContactUrl } = contact;
  const hasCrmLink = !!(salesforceContactUrl || salesforceLeadUrl || hubspotContactUrl);
  const isContactDetailsEmpty = !contact.title && (!withCompanyName || !contact.companyName) && !contact.country;

  const handleSelectContact = () => {
    if (isSelected) {
      appStore.removeFromSelectedContacts(contact);
    } else {
      appStore.addToSelectedContacts(contact);
    }
  };

  const isCheckboxVisible = variant === 'listItem';
  const isCheckboxDisabled = (isTrialOrAfterTrial && quota.isDepleted) || isRevealError || !isRevealAvailable;

  return (
    <StyledContactItem
      data-contact-id={contact.id || contact.linkedin}
      variant={variant}
      data-automation="ContactItem"
      isCheckboxVisible={isCheckboxVisible}
    >
      {isCheckboxVisible ? (
        <Checkbox
          isChecked={isSelected}
          onClick={handleSelectContact}
          disabled={isCheckboxDisabled}
          dataAutomation="ContactItemCheckbox"
        />
      ) : null}
      <StyledContactItemContainer>
        <StyledContactHeader hasCrmLink={hasCrmLink} isTrialOrAfterTrial={isTrialOrAfterTrial}>
          <StyledContactName data-automation="ContactName">{contact.name}</StyledContactName>
          {isCheckboxVisible && contact.linkedin ? (
            <StyledContactLinkedinTooltip content={t('contact_card.linkedin.tooltip')} minWidth={170} placement="top">
              <a href={contact.linkedin} target="_blank" rel="noreferrer" onMouseDown={handleTrackLinkedinLinkClick}>
                <SWReactIcons iconName="linkedin-circle-blue" size="xs" />
              </a>
            </StyledContactLinkedinTooltip>
          ) : null}
          {contact.accuracyGrade ? (
            <ContactGrade grade={contact.accuracyGrade} onHover={() => handleTrackChipHover('grade score')} />
          ) : null}
          {hasMatchedProfiles(contact) ? <PersonaFit onHover={() => handleTrackChipHover('persona fit')} /> : null}
        </StyledContactHeader>
        <StyledContactItemColumn isTrialOrAfterTrial={isTrialOrAfterTrial}>
          {isContactDetailsEmpty ? null : (
            <StyledContactDetails>
              <StyledContactTitle data-automation="ContactTitle">
                {contact.title}
                {withCompanyName && contact.companyName ? ` at ${contact.companyName}` : null}
              </StyledContactTitle>
              <StyledContactCountry data-automation="ContactCountry">{contact.country}</StyledContactCountry>
            </StyledContactDetails>
          )}
          <StyledContactActionsContainer>
            <ContactActionsContainer
              contact={contact}
              setShouldAnimatePrivateDataMount={setShouldAnimatePrivateDataMount}
            />
          </StyledContactActionsContainer>
        </StyledContactItemColumn>
        {hasPrivateData ? (
          <PrivateDataView
            privateData={contact.privateData!}
            animatedMount={shouldAnimatePrivateDataMount}
            variant={variant}
            onCopyClick={handleTrackCopyClick}
          />
        ) : (
          <BlurredPrivateDataView contact={contact} variant={variant} />
        )}
      </StyledContactItemContainer>
    </StyledContactItem>
  );
};
